import React, { useState } from 'react';
import { object, func, bool, number } from 'prop-types';

import * as JourneyMapForm from 'apps/admin/styles/components/styled-journeymap-form';
import { resolve } from 'library/helpers/path-resolver';
import MarkdownTextarea from 'library/components/form-elements/markdown-textarea';
import CreateUpdateStepMutation from 'apps/admin/pages/journey-map/queries/create-update-step-mutation';
import { CreateStepCompletionActionMutation } from 'apps/admin/pages/journey-map/queries/create-step-completion-action-mutation';
import { DestroyStepCompletionActionMutation } from 'apps/admin/pages/journey-map/queries/destroy-step-completion-action-mutation';
import { StepActionOutcomeAttacher } from 'apps/admin/pages/journey-map/step-action-outcome-attacher';
import Notification, {
  Error as NotificationError,
  NotificationRender,
} from 'library/components/notification';
import InternalQuery from 'apps/admin/util/query';
import StepQuestionForm from 'apps/admin/pages/journey-map/step-question-form';

import StepFileOptions from './components/step-file-options';
import StepLinkOptions from './components/step-link-options';
import StepWidgetOptions from './components/step-widget-options';

import withData from 'util/hoc/with-data';

const STEP_RESOURCE_OPTIONS = {
  link: StepLinkOptions,
  attached_file: StepFileOptions,
  widget: StepWidgetOptions,
};

const StepForm = ({
  reload_graph,
  graph_data,
  graph_loading,
  graph_update,
  graph_query,
  onComplete = () => {},
  onCancel = () => {},
  goalId,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const loading = isLoading || graph_loading;
  const {
    id,
    name = '',
    description = '',
    step_questions = [],
    step_resources = [],
    step_completion_actions = [],
  } = resolve(graph_data, 'step', {});

  const outcomes = graph_data?.outcomes;

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsLoading(true);

    try {
      const response = await InternalQuery(
        CreateUpdateStepMutation(graph_data?.step, goalId)
      );

      if (resolve(response, 'data/create_update/errors', []).length) {
        throw new Error(
          `Error creating step: ${resolve(
            response,
            'data/create_update/errors',
            []
          )
            .map((e) => e.message)
            .join('\n')}`
        );
      }
      if (resolve(response, 'errors', []).length) {
        throw new Error(
          `Error creating step: ${resolve(response, 'errors', [])
            .map((e) => e.message)
            .join('\n')}`
        );
      }

      onComplete();
    } catch (e) {
      Notification.new(
        { timeout: 5000 },
        <NotificationError>{e.toString()}</NotificationError>
      );
      console.error(e);
    }

    setIsLoading(false);
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    const step = { ...resolve(graph_data, 'step', {}) };
    step[name] = value;
    graph_update({ step, outcomes });
  };

  const addQuestionToGraph = (e, question) => {
    e.preventDefault();
    e.stopPropagation();

    if (!question) {
      NotificationRender('error', 'Please Select a Question');
      return;
    }

    const step = { ...resolve(graph_data, 'step', {}) };
    step.step_questions.push({
      question: {
        id: question?.question?.id,
        internal_title: question?.question?.internal_title,
      },
    });

    graph_update({ step, outcomes });
  };

  const removeQuestion = (questionId) => {
    const step = { ...resolve(graph_data, 'step', {}) };
    const questionIndex = step.step_questions.findIndex(
      (c) => c.id == questionId
    );
    step.step_questions.splice(questionIndex, 1);

    graph_update({ step, outcomes });
  };

  const handleResourceChange = (e) => {
    const { value, name } = e.target;
    console.log(e, value, name);
    const step = { ...resolve(graph_data, 'step', {}) };
    step.step_resources = step.step_resources || [];
    step.step_resources[0] = step.step_resources[0] || {};
    if (name == 'file_name') {
      step.step_resources[0]['name'] = value[1];
      step.step_resources[0]['title'] = value[2];
    } else {
      step.step_resources[0][name] = value;
    }
    graph_update({ step, outcomes });
  };

  const createOutcomeStepAction = async (values, onSuccess) => {
    setIsLoading(true);

    try {
      const request = await InternalQuery(
        CreateStepCompletionActionMutation(values)
      );
      const response = request?.data?.create_step_completion_action;

      if (response?.success) {
        NotificationRender('success', 'Action successfully created');
        reload_graph(true, true);
        onSuccess();
      } else {
        NotificationRender('error', response?.errors?.join(', '));
      }
    } catch (e) {
      console.error(e);
      NotificationRender('error', 'Something went wrong, please try again.');
    }

    setIsLoading(false);
  };

  const destroyOutcomeStepAction = async (stepActionId) => {
    setIsLoading(true);

    try {
      const request = await InternalQuery(
        DestroyStepCompletionActionMutation(stepActionId)
      );
      const response = request?.data?.destroy_step_completion_action;

      if (response?.success) {
        NotificationRender('success', 'Action successfully removed');
        reload_graph(true, true);
      } else {
        NotificationRender('error', response?.errors?.join(', '));
      }
    } catch (e) {
      console.error(e);
      NotificationRender('error', 'Something went wrong, please try again.');
    }

    setIsLoading(false);
  };

  const buttonText = id ? 'Update' : 'Create';
  const resourceType = resolve(step_resources, '0/resource_type', 'none');
  const StepResourceOptions = resolve(
    STEP_RESOURCE_OPTIONS,
    resourceType,
    () => null
  );
  const stepResource = resolve(step_resources, '0', {});

  return (
    <JourneyMapForm.StyledForm onSubmit={handleSubmit}>
      <JourneyMapForm.StyledFormContainer>
        <JourneyMapForm.FormRow>
          <JourneyMapForm.NameLabel>
            Name
            <JourneyMapForm.NameInput
              value={name}
              required
              placeholder="Name"
              name="name"
              onChange={handleChange}
              disabled={loading}
            />
          </JourneyMapForm.NameLabel>
          <JourneyMapForm.NameLabel>
            Resource Type
            <JourneyMapForm.Select
              value={resourceType}
              defaultValue="none"
              placeholder="Resource Type"
              name="resource_type"
              onChange={handleResourceChange}
            >
              <option value="none">None</option>
              <option value="attached_file"> PDF </option>
              <option value="link">Link</option>
              <option value="widget">Widget</option>
            </JourneyMapForm.Select>
          </JourneyMapForm.NameLabel>
        </JourneyMapForm.FormRow>
        <StepResourceOptions
          resource={stepResource}
          onChange={handleResourceChange}
        />
        <JourneyMapForm.DescriptionLabel>
          Description
          <MarkdownTextarea
            component={JourneyMapForm.DescriptionTextArea}
            previewComponent={JourneyMapForm.MarkdownTextArea}
            value={description}
            name="description"
            placeholder="Description"
            required
            onChange={handleChange}
            disabled={loading}
          />
        </JourneyMapForm.DescriptionLabel>
        <JourneyMapForm.FormRow>
          <StepQuestionForm
            onAdd={addQuestionToGraph}
            onRemove={removeQuestion}
            questions={step_questions}
          />
          <JourneyMapForm.DescriptionLabel>
            Step Actions
            <StepActionOutcomeAttacher
              stepId={id}
              handleCreateStepAction={createOutcomeStepAction}
              handleDestroyStepAction={destroyOutcomeStepAction}
              stepActions={step_completion_actions}
              outcomes={outcomes}
              loading={loading}
            />
          </JourneyMapForm.DescriptionLabel>
        </JourneyMapForm.FormRow>
      </JourneyMapForm.StyledFormContainer>
      <JourneyMapForm.FooterRow>
        <JourneyMapForm.SubmitButtons>
          <input
            className="button"
            value="Cancel"
            onClick={onCancel}
            disabled={loading}
          />
          <input
            type="submit"
            className="button"
            value={buttonText}
            onSubmit={handleSubmit}
            disabled={loading}
          />
        </JourneyMapForm.SubmitButtons>
      </JourneyMapForm.FooterRow>
    </JourneyMapForm.StyledForm>
  );
};

StepForm.propTypes = {
  graph_data: object.isRequired,
  graph_loading: bool,
  graph_update: func.isRequired,
  graph_query: func,
  onCancel: func,
  onComplete: func,
  goalId: number,
};

StepForm.query = async (token, { stepId }) => {
  if (!stepId) return;

  return await InternalQuery(`
    {
      step(id:${stepId}) {
        id
        name
        description

        step_resources {
          id
          resource_type
          name
          title

          service_provider_categories {
            id
            name
          }
        }

        step_questions {
          id
          step_id
          question{
            id
            internal_title
            text
          }
        }

        step_completion_actions {
          id
          action_type
          outcome_id
        }
      }

      outcomes {
        id
        name
        description
      }
    }
  `);
};

export default withData(StepForm);
